import React, { useState } from 'react'
import { Alert, Box, Button, Divider, Flex, Grid, PasswordInput, Select, Stack, Text, TextInput, Title } from '@mantine/core'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useActions } from 'src/state'
import { Tenant_Type_Enum } from 'src/state/gql/_types'

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming",
]

const TENANT_TYPE_OPTIONS = [
    { value: Tenant_Type_Enum.Bar, label: 'Bar' },
    { value: Tenant_Type_Enum.Business, label: 'Business' },
    { value: Tenant_Type_Enum.Club, label: 'Club' },
    { value: Tenant_Type_Enum.EventSpace, label: 'Event Space' },
    { value: Tenant_Type_Enum.HospitalityGroup, label: 'Hospitality Group' },
    { value: Tenant_Type_Enum.Hotel, label: 'Hotel' },
    { value: Tenant_Type_Enum.Restaurant, label: 'Restaurant' },
]

const validationSchema = yup.object({
    email: yup.string().required('Email is required').email('Invalid email address'),
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters').matches(/[a-zA-Z]/, 'Password must contain at least one letter'),
    verify_password: yup.string().required('Please confirm your password').oneOf([yup.ref('password')], 'Passwords do not match'),
    tenant_name: yup.string().required('Venue name is required'),
    tenant_type: yup.string().required('Venue type is required'),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
})

const VenueSignUpForm: React.FC = () => {

    const signUpAction = useActions().signUpAction
    const [formError, setFormError] = useState<string | null>(null)

    const formik = useFormik({
        initialValues: {
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            verify_password: '',
            tenant_name: '',
            tenant_type: '',
            city: '',
            state: '',
        },
        validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: async ({ verify_password: _, ...values }, { setErrors }) => {
            setFormError(null)
            const res = await signUpAction({ ...values, signup_mode: 'venue' })
            if (!res.success) {
                if (res.errors.form) setFormError(res.errors.form)
                if (Object.keys(res.errors.fields).length) setErrors(res.errors.fields)
                return
            }
            window.location.assign('/verify-email')
        },
    })

    return (
        <form onSubmit={formik.handleSubmit}>
            {formError && (
                <Alert color="red" mb="lg">{formError}</Alert>
            )}

            <Flex
                direction={{ base: 'column', md: 'row' }}
                align="stretch"
            >
                {/* Left — About Your Venue */}
                <Box flex={1} p={{ base: 'md', md: 'xl' }}>
                    <Stack gap="sm" maw={360} mx="auto">
                        <Box mb="xs">
                            <Title order={3} fw={800}>About Your Venue</Title>
                            <Text c="dimmed" size="sm">
                                Tell us a bit about the venue you're registering.
                            </Text>
                        </Box>

                        <TextInput
                            label="Venue Name"
                            name="tenant_name"
                            placeholder="e.g. The Blue Note"
                            value={formik.values.tenant_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tenant_name && formik.errors.tenant_name}
                            radius="md"
                        />

                        <Select
                            label="Venue Type"
                            data={TENANT_TYPE_OPTIONS}
                            placeholder="Select a type"
                            value={formik.values.tenant_type}
                            onChange={(value) => formik.setFieldValue('tenant_type', value ?? '')}
                            onBlur={() => formik.setFieldTouched('tenant_type', true)}
                            error={formik.touched.tenant_type && formik.errors.tenant_type}
                            radius="md"
                        />

                        <TextInput
                            label="City"
                            name="city"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.city && formik.errors.city}
                            radius="md"
                        />

                        <Select
                            label="State"
                            data={US_STATES}
                            value={formik.values.state}
                            onChange={(value) => formik.setFieldValue('state', value ?? '')}
                            onBlur={() => formik.setFieldTouched('state', true)}
                            error={formik.touched.state && formik.errors.state}
                            searchable
                            radius="md"
                        />
                    </Stack>
                </Box>

                <Divider orientation="vertical" visibleFrom="md" color="gray.2" />
                <Divider orientation="horizontal" hiddenFrom="md" color="gray.2" />

                {/* Right — Create Your Account */}
                <Box flex={1} p={{ base: 'md', md: 'xl' }}>
                    <Stack gap="sm" maw={360} mx="auto">
                        <Box mb="xs">
                            <Title order={3} fw={800}>Create Your Account</Title>
                            <Text c="dimmed" size="sm">
                                Set up your personal login credentials.
                            </Text>
                        </Box>

                        <TextInput
                            type="email"
                            label="Email"
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.email && formik.errors.email}
                            autoComplete="email"
                            radius="md"
                        />

                        <Grid>
                            <Grid.Col span={6}>
                                <TextInput
                                    label="First Name"
                                    name="first_name"
                                    value={formik.values.first_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.first_name && formik.errors.first_name}
                                    radius="md"
                                />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <TextInput
                                    label="Last Name"
                                    name="last_name"
                                    value={formik.values.last_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.last_name && formik.errors.last_name}
                                    radius="md"
                                />
                            </Grid.Col>
                        </Grid>

                        <PasswordInput
                            label="Password"
                            name="password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.password && formik.errors.password}
                            autoComplete="new-password"
                            radius="md"
                        />

                        <PasswordInput
                            label="Confirm Password"
                            name="verify_password"
                            value={formik.values.verify_password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.verify_password && formik.errors.verify_password}
                            autoComplete="new-password"
                            radius="md"
                        />

                        <Button
                            type="submit"
                            fullWidth
                            loading={formik.isSubmitting}
                            disabled={!formik.isValid || formik.isSubmitting}
                            mt="sm"
                            radius="xl"
                            size="md"
                        >
                            Sign Up
                        </Button>
                    </Stack>
                </Box>
            </Flex>
        </form>
    )
}

export default VenueSignUpForm
